<template>
  <PageHeader title="Missed Conversations">
    <template #right>
      <div class="flex items-center gap-3">
        <Select
          v-model="callCenterSelected"
          :options="callCentersAllowed"
          value-key="id"
          label-key="name"
          :placeholder="`Select a ${$t('ui.entities.departments.callCenter')}`"
          class="min-w-56"
          icon="headset"
          :readonly="callCentersAllowed.length == 1"
          @select="updateCallCenterSelected"
        ></Select>
        <Button
          label="Refresh"
          severity="secondary"
          outlined
          icon="refresh"
          class="flex-shrink-0"
          :is-loading="conversationsApiRequest.isLoading.value"
          @click="fetchConversations"
        />
      </div>
    </template>
  </PageHeader>

  <div class="flex flex-col gap-8">
    <!-- Unactioned Conversations -->
    <div v-if="unactionedConversations?.length > 0">
      <div class="flex flex-col gap-3">
        <MissedConversationItem
          v-for="item in unactionedConversations"
          :key="item.id"
          :model-value="item"
          :agreements-list="agreementsList"
          :search-term="linkAgreementSearchTerm"
          @update:search-term="handleLinkAgreementSearchTermUpdate"
          @link-to-customer="linkToCustomer"
          @call-back="callBack"
          @update:model-value="updateConversationItem"
          @accept-conversation="acceptConversation"
          @release-conversation="releaseConversation"
          @complete-conversation="completeConversation"
        />
      </div>
    </div>
    <Empty
      v-else
      title="No unactioned conversations"
      icon="check-double"
    />

    <!-- Actioned Conversations -->
    <div>
      <div class="text-lg mb-2">Actioned</div>
      <div
        v-if="actionedConversations?.length > 0"
        class="flex flex-col gap-2"
      >
        <MissedConversationItem
          v-for="item in actionedConversations"
          :key="item.id"
          :model-value="item"
          :agreements-list="agreementsList"
          :search-term="linkAgreementSearchTerm"
          @update:search-term="handleLinkAgreementSearchTermUpdate"
          @update:model-value="updateConversationItem"
        />
      </div>
      <Empty
        v-else
        title="No actioned conversations"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onUnmounted, inject } from 'vue'
import dayjs from 'dayjs'

import PageHeader from '@/components/page/PageHeader.vue'
import Select from '@/components/forms/Select.vue'
import Empty from '@/components/empty/Empty.vue'
import MissedConversationItem from '@/components/conversations/MissedConversationItem.vue'

// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { useCallCenterStore } from '@/stores/CallCenterStore'
// composables
import useApiRequest from '@/composables/useApiRequest'
import { alertToast } from '@/utilities/notification'
import useMeilisearch from '@/composables/useMeilisearch'

// components
import Button from '@/components/button/Button.vue'

const authStore = useAuthStore()
const callCenterStore = useCallCenterStore()
const emitter = inject('emitter')
const callCentersAllowed = callCenterStore.callCenters
const callCenterSelected = ref(null)

const dealershipsAllowed = ref(authStore.accessAllowed.dealerships.map(dealership => dealership.id))
const dealershipsAllowedString = dealershipsAllowed.value.join(',')

const linkAgreementApiRequest = useApiRequest()

const linkAgreementSearchTerm = ref('')
const meilisearchResults = ref([])
const { createSearch } = useMeilisearch()
const { scheduleSearch, dispose: disposeSearch } = createSearch(
  async ({ client, signal, query }) => {
    return client.index('agreements').search(
      query,
      {
        filter: 'dealershipId IN [' + dealershipsAllowedString + ']'
      },
      { signal }
    )
  },
  {
    debounceMs: 300,
    onClear: () => {
      meilisearchResults.value = []
    },
    onResult: results => {
      meilisearchResults.value = results
    }
  }
)

// Create a computed property to transform the data
const agreementsList = computed(() => {
  if (meilisearchResults.value?.hits != undefined) {
    return meilisearchResults.value?.hits
  }
  return false
})

onUnmounted(() => {
  disposeSearch()
})

/**
 * Handles updates to the link agreement search term.
 * @param {string} newSearchTerm
 */
function handleLinkAgreementSearchTermUpdate(newSearchTerm) {
  linkAgreementSearchTerm.value = newSearchTerm
  scheduleSearch(newSearchTerm)
}

const conversations = ref(null)
const conversationsApiRequest = useApiRequest()

function fetchConversations() {
  if (!callCenterSelected.value) {
    return
  }

  conversationsApiRequest
    .send({
      endpoint: '/v1/comms/missed',
      method: 'GET',
      params: { callCenterId: callCenterSelected.value }
    })
    .then(response => {
      if (response.success) {
        conversations.value = response.data
      }
    })
}

function getDelaySeverity(timestamp) {
  // Calculate time since using
  const delay = dayjs().diff(timestamp, 'minutes')

  let delaySeverity = ''
  // If between 30-59 minutes, return 'delayed-warning'
  if (delay >= 30 && delay <= 59) {
    delaySeverity = 'warning'
  }
  // If greater than 60 minutes, return 'delayed-danger'
  if (delay >= 60) {
    delaySeverity = 'danger'
  }

  return delaySeverity
}

const computedConversations = computed(() => {
  if (!conversations.value) return []

  const conversationsList = []

  conversations.value.forEach(item => {
    conversationsList.push({
      ...item,
      delaySeverity: getDelaySeverity(item.createdAt)
    })
  })

  // Sort conversations by createdAt ascending
  conversationsList.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))

  return conversationsList
})

const unactionedConversations = computed(() => {
  return computedConversations.value.filter(item => !item.isActioned)
})

const actionedConversations = computed(() => {
  return computedConversations.value.filter(item => !!item.isActioned)
})

/**
 * Update a conversation item in the conversations array
 * @param {Object} updatedItem - The updated conversation item
 */
function updateConversationItem(updatedItem) {
  if (!conversations.value) return

  const index = conversations.value.findIndex(item => item.id === updatedItem.id)
  if (index !== -1) {
    conversations.value[index] = { ...conversations.value[index], ...updatedItem }
  }
}

if (!localStorage.getItem('report-filter-call-center-selected')) {
  localStorage.setItem('report-filter-call-center-selected', callCentersAllowed[0]?.id)
  callCenterSelected.value = callCentersAllowed[0]?.id
} else {
  callCenterSelected.value = parseInt(localStorage.getItem('report-filter-call-center-selected'))
}

fetchConversations()

function updateCallCenterSelected() {
  localStorage.setItem('report-filter-call-center-selected', callCenterSelected.value)
  fetchConversations()
}

function linkToCustomer(comm, selectedItem) {
  let endpoint = null
  if (comm.type == 'call') {
    endpoint = '/v1/comms/calls/' + comm.id
  } else if (comm.type == 'sms') {
    endpoint = '/v1/comms/sms/' + comm.id
  } else if (comm.type == 'email') {
    endpoint = '/v1/comms/emails/' + comm.id
  }

  linkAgreementApiRequest
    .send({
      endpoint: endpoint,
      data: {
        agreementId: parseInt(selectedItem.agreementId)
      },
      method: 'PATCH'
    })
    .then(() => {
      comm.agreementId = selectedItem.agreementId
      comm.customerName = selectedItem.name

      alertToast('Customer linked', null, 'success')
    })
}

/**
 * Handles callback action - opens ContactPanel with phone numbers
 * @param comm - Conversation object with commFrom, commTo, and optional agreementId
 */
async function callBack(comm) {
  if (!callCenterStore.selectedCallCenter) {
    alertToast('Failed to load phone', 'No call center selected', 'danger')
    return
  }

  // For callback, swap from/to: call FROM call center TO customer
  let fromNumber = comm.commTo // Call center number (was originally "to")
  let fromName = comm.recipient?.name || 'Unknown'
  const toNumber = comm.commFrom // Customer number (was originally "from")
  const toName = comm.customerName || comm.recipient?.name || 'Unknown'

  // If agreementId exists, fetch agreement to get proper comms config
  if (comm.agreementId) {
    try {
      const agreementResponse = await useApiRequest().send({
        endpoint: '/v1/agreements/' + comm.agreementId,
        method: 'GET'
      })

      if (agreementResponse.success && agreementResponse.data?.dealership?.commsConfig) {
        const commsConfig = agreementResponse.data.dealership.commsConfig.find(
          config => config.callCenterId.toString() === callCenterStore.selectedCallCenter
        )

        if (commsConfig) {
          fromNumber = commsConfig.phoneNumber
          fromName = commsConfig.phoneNumberName || fromName
        }
      }
    } catch (err) {
      // If fetching fails, continue with comm.commTo as fallback
      console.warn('Failed to fetch agreement for comms config:', err)
    }
  }

  // Emit event to open ContactPanel
  emitter.emit('phone-set-numbers', {
    from: {
      number: fromNumber,
      name: fromName
    },
    to: {
      number: toNumber,
      name: toName,
      agreementId: comm.agreementId || undefined
    }
  })
}

function acceptConversation(comm) {
  let endpoint = null
  if (comm.type == 'call') {
    endpoint = '/v1/comms/calls/' + comm.id
  } else if (comm.type == 'sms') {
    endpoint = '/v1/comms/sms/' + comm.id
  } else if (comm.type == 'email') {
    endpoint = '/v1/comms/emails/' + comm.id
  }
  useApiRequest()
    .send({ endpoint: endpoint, method: 'PATCH', data: { acceptedBy: authStore.details.id, acceptedAt: new Date() } })
    .then(() => {
      // Construct user object from authStore.details to match component expectations
      const acceptedByUser = {
        id: authStore.details.id,
        name: authStore.details.name || authStore.details.fullName,
        profilePicUrl: authStore.details.profilePicUrl,
        initials: authStore.details.initials
      }
      // Update the source conversations array to ensure reactivity
      updateConversationItem({
        id: comm.id,
        acceptedBy: acceptedByUser,
        acceptedAt: new Date(),
        isActioned: false
      })
    })
}

function releaseConversation(comm) {
  let endpoint = null
  if (comm.type == 'call') {
    endpoint = '/v1/comms/calls/' + comm.id
  } else if (comm.type == 'sms') {
    endpoint = '/v1/comms/sms/' + comm.id
  } else if (comm.type == 'email') {
    endpoint = '/v1/comms/emails/' + comm.id
  }
  useApiRequest()
    .send({ endpoint: endpoint, method: 'PATCH', data: { isActioned: false, acceptedBy: null, acceptedAt: null } })
    .then(() => {
      // Update the source conversations array to ensure reactivity
      updateConversationItem({
        id: comm.id,
        acceptedBy: null,
        acceptedAt: null,
        isActioned: false
      })
    })
}

function completeConversation(comm) {
  let endpoint = null
  if (comm.type == 'call') {
    endpoint = '/v1/comms/calls/' + comm.id
  } else if (comm.type == 'sms') {
    endpoint = '/v1/comms/sms/' + comm.id
  } else if (comm.type == 'email') {
    endpoint = '/v1/comms/emails/' + comm.id
  }
  useApiRequest()
    .send({
      endpoint: endpoint,
      method: 'PATCH',
      data: { isActioned: true, acceptedBy: authStore.details.id, acceptedAt: new Date() }
    })
    .then(() => {
      // Construct user object from authStore.details to match component expectations
      const acceptedByUser = {
        id: authStore.details.id,
        name: authStore.details.name || authStore.details.fullName,
        profilePicUrl: authStore.details.profilePicUrl,
        initials: authStore.details.initials
      }
      // Update the source conversations array to ensure reactivity
      updateConversationItem({
        id: comm.id,
        acceptedBy: acceptedByUser,
        acceptedAt: new Date(),
        isActioned: true
      })
    })
}
</script>

<style scoped></style>
